import { type Role, roles, type UserIdentity } from "@groundspark/browser-auth";

export type { Role };
export { roles };

export interface AdminSummary {
  readonly userCount: number;
  readonly pendingInvitationCount: number;
  readonly auditEventCount: number;
}

export function parseAdminSummary(value: unknown): AdminSummary {
  const record = objectValue(value, "administration summary");
  return {
    userCount: integerField(record, "user_count", 0),
    pendingInvitationCount: integerField(record, "pending_invitation_count", 0),
    auditEventCount: integerField(record, "audit_event_count", 0),
  };
}

export interface Branch {
  readonly id: string;
  readonly name: string;
}

export interface BranchSummary extends Branch {
  readonly memberCount: number;
  readonly projectCount: number;
  readonly leafletCount: number;
  readonly pendingInvitationCount: number;
}

export interface Group {
  readonly id: string;
  readonly name: string;
}

export interface GroupSummary extends Group {
  readonly memberCount: number;
}

export interface AdminUser extends UserIdentity {
  readonly firstName: string;
  readonly lastName: string;
  readonly phoneNumber: string;
  readonly pendingEmail: string | null;
  readonly branch: Branch;
  readonly groups: readonly Group[];
  readonly role: Role;
  readonly accessVersion: number;
  readonly electoralRegisterIds: readonly string[];
  readonly createdAt: Date;
}

export interface UserChanges {
  readonly firstName: string;
  readonly lastName: string;
  readonly phoneNumber: string;
  readonly email: string;
  readonly branchId: string;
  readonly groupIds: ReadonlySet<string>;
  readonly role: Role;
  readonly registerIds: ReadonlySet<string>;
}

export interface ElectoralRegister {
  readonly id: string;
  readonly label: string;
  readonly snapshotId: string;
}

export type InvitationKind = "email" | "organiser_qr";

export interface AdminInvitation {
  readonly branch: Branch;
  readonly id: string;
  readonly kind: InvitationKind;
  readonly intendedEmail: string | null;
  readonly issuedAt: Date;
  readonly expiresAt: Date;
  readonly acceptedUserId: string | null;
  readonly acceptedAt: Date | null;
  readonly revokedAt: Date | null;
}

export interface AdminAuditEvent {
  readonly id: number;
  readonly action: string;
  readonly occurredAt: Date;
  readonly actorKind: string;
  readonly actorIdentifier: string;
  readonly targetUserId: string | null;
  readonly invitationId: string | null;
  readonly electoralRegisterId: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

export interface Page<T> {
  readonly items: readonly T[];
  readonly nextCursor: string | null;
}

export function parseAdminUser(value: unknown): AdminUser {
  const record = objectValue(value, "administrator user");
  const id = nonEmptyStringField(record, "id");
  if (id.includes("/")) {
    throw new TypeError("administrator user has an invalid ID");
  }
  const registerIds = arrayField(record, "electoral_register_ids").map((candidate) =>
    nonEmptyString(candidate, "electoral-register ID"),
  );
  if (new Set(registerIds).size !== registerIds.length) {
    throw new TypeError("administrator user has duplicate roll assignments");
  }
  const groups = arrayField(record, "groups").map(parseGroup);
  if (new Set(groups.map((group) => group.id)).size !== groups.length) {
    throw new TypeError("administrator user has duplicate group memberships");
  }
  return {
    id,
    email: nonEmptyStringField(record, "email"),
    displayName: nonEmptyStringField(record, "display_name"),
    firstName: nonEmptyStringField(record, "first_name"),
    lastName: nonEmptyStringField(record, "last_name"),
    phoneNumber: nonEmptyStringField(record, "phone_number"),
    pendingEmail: optionalStringField(record, "pending_email"),
    branch: parseBranch(record.branch),
    groups,
    role: roleField(record, "role"),
    accessVersion: integerField(record, "access_version", 0),
    electoralRegisterIds: registerIds,
    createdAt: dateField(record, "created_at"),
  };
}

export function parseElectoralRegister(value: unknown): ElectoralRegister {
  const record = objectValue(value, "electoral register");
  const id = nonEmptyStringField(record, "id");
  const label = nonEmptyStringField(record, "label");
  const snapshotId = nonEmptyStringField(record, "snapshot_id");
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(id) || id.length > 64) {
    throw new TypeError("electoral register has an invalid ID");
  }
  if (label.trim() !== label || !/^[0-9a-f]{64}$/.test(snapshotId)) {
    throw new TypeError("electoral register metadata is invalid");
  }
  return { id, label, snapshotId };
}

export function parseAdminInvitation(value: unknown): AdminInvitation {
  const record = objectValue(value, "invitation");
  const kind = nonEmptyStringField(record, "kind");
  if (kind !== "email" && kind !== "organiser_qr") {
    throw new TypeError("invitation has an invalid kind");
  }
  return {
    id: nonEmptyStringField(record, "id"),
    kind,
    branch: parseBranch(record.branch),
    intendedEmail: optionalStringField(record, "intended_email"),
    issuedAt: dateField(record, "issued_at"),
    expiresAt: dateField(record, "expires_at"),
    acceptedUserId: optionalStringField(record, "accepted_user_id"),
    acceptedAt: optionalDateField(record, "accepted_at"),
    revokedAt: optionalDateField(record, "revoked_at"),
  };
}

export function parseAdminAuditEvent(value: unknown): AdminAuditEvent {
  const record = objectValue(value, "audit event");
  return {
    id: integerField(record, "id", 0),
    action: nonEmptyStringField(record, "action"),
    occurredAt: dateField(record, "occurred_at"),
    actorKind: stringField(record, "actor_kind"),
    actorIdentifier: stringField(record, "actor_identifier"),
    targetUserId: optionalStringField(record, "target_user_id"),
    invitationId: optionalStringField(record, "invitation_id"),
    electoralRegisterId: optionalStringField(record, "electoral_register_id"),
    details: objectValue(record.details, "audit details"),
  };
}

export function parsePage<T>(
  value: unknown,
  parseItem: (candidate: unknown) => T,
): Page<T> {
  const record = objectValue(value, "administration page");
  return {
    items: arrayField(record, "items").map(parseItem),
    nextCursor: optionalStringField(record, "next_cursor"),
  };
}

export function invitationStatus(
  invitation: AdminInvitation,
  now = new Date(),
): "accepted" | "revoked" | "expired" | "pending" {
  if (invitation.acceptedAt !== null) return "accepted";
  if (invitation.revokedAt !== null) return "revoked";
  if (invitation.expiresAt.getTime() <= now.getTime()) return "expired";
  return "pending";
}

function objectValue(value: unknown, description: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${description} must be an object`);
  }
  return value as Record<string, unknown>;
}

function arrayField(record: Record<string, unknown>, field: string): unknown[] {
  const value = record[field];
  if (!Array.isArray(value)) {
    throw new TypeError(`${field} must be an array`);
  }
  return value;
}

function stringField(record: Record<string, unknown>, field: string): string {
  const value = record[field];
  if (typeof value !== "string") {
    throw new TypeError(`${field} must be a string`);
  }
  return value;
}

function nonEmptyStringField(record: Record<string, unknown>, field: string): string {
  return nonEmptyString(record[field], field);
}

function nonEmptyString(value: unknown, description: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new TypeError(`${description} must be a non-empty string`);
  }
  return value;
}

function optionalStringField(
  record: Record<string, unknown>,
  field: string,
): string | null {
  const value = record[field];
  if (value === null || value === undefined) return null;
  return nonEmptyString(value, field);
}

function integerField(
  record: Record<string, unknown>,
  field: string,
  minimum: number,
): number {
  const value = record[field];
  if (!Number.isSafeInteger(value) || (value as number) < minimum) {
    throw new TypeError(`${field} must be an integer of at least ${minimum}`);
  }
  return value as number;
}

function roleField(record: Record<string, unknown>, field: string): Role {
  const value = record[field];
  if (!roles.some((role) => role === value)) {
    throw new TypeError(`${field} must be a Groundspark role`);
  }
  return value as Role;
}

function dateField(record: Record<string, unknown>, field: string): Date {
  const value = stringField(record, field);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new TypeError(`${field} must be an ISO date`);
  }
  return date;
}

function optionalDateField(
  record: Record<string, unknown>,
  field: string,
): Date | null {
  if (record[field] === null || record[field] === undefined) return null;
  return dateField(record, field);
}

export function parseBranch(value: unknown): Branch {
  return parseNamedEntity(value, "branch");
}

export function parseGroup(value: unknown): Group {
  return parseNamedEntity(value, "group");
}

function parseNamedEntity(
  value: unknown,
  description: string,
): { id: string; name: string } {
  const record = objectValue(value, description);
  const id = nonEmptyStringField(record, "id");
  if (id.length > 64 || id.includes("/") || id.trim() !== id)
    throw new TypeError(`invalid ${description} ID`);
  return { id, name: nonEmptyStringField(record, "name") };
}

export function parseGroupSummary(value: unknown): GroupSummary {
  const record = objectValue(value, "group summary");
  return {
    ...parseGroup(record),
    memberCount: integerField(record, "member_count", 0),
  };
}

export function parseBranchSummary(value: unknown): BranchSummary {
  const record = objectValue(value, "branch summary");
  return {
    ...parseBranch(record),
    memberCount: integerField(record, "member_count", 0),
    projectCount: integerField(record, "project_count", 0),
    leafletCount: integerField(record, "leaflet_count", 0),
    pendingInvitationCount: integerField(record, "pending_invitation_count", 0),
  };
}
